import { Add, Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { ReactNode, useState } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PercentageField } from '~/components/GenericForm/PercentageField';
import { CostCenter } from '~/data/costCenters';
import { CustomDataDialogComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/CostCenterPercentage/CustomDataDialogComponent';
import { IndexedCustomFields } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';

import { useCostCenterPercentage } from './useCostCenterPercentage';
import { useSxStyles } from './useStyles';

type Props = {
  scheduleFormMethods: UseFormReturn<Form>;
  additionalCostCenters: CostCenter[];
  getCostCenterValues: (positionUnit?: number) => CostCenter[];
  selectedCostCenterId: number;
  index: number;
  customFields: IndexedCustomFields;
  onAdditionalCostCenterAdd: (id: number) => void;
  deleteSelectedCC: (id: number) => void;
};
export const CostCenterPercentage = ({
  selectedCostCenterId,
  getCostCenterValues,
  onAdditionalCostCenterAdd,
  additionalCostCenters,
  deleteSelectedCC,
  scheduleFormMethods,
  index,
  customFields,
}: Props) => {
  const { getValues, control } = scheduleFormMethods;
  // @ts-ignore
  useWatch({
    control,
    name: `schedule.${index}.empPosition.fInternalPositionUnit`,
  });
  useWatch({
    control,
    name: `schedule.${index}.empPosition.fEmpCostCenter`,
  });

  const fInternalPositionUnitValue = getValues(
    `schedule.${index}.empPosition.fInternalPositionUnit`,
  );
  const costCenters = getCostCenterValues(fInternalPositionUnitValue);
  const {
    selectedCostCenterData,
    setAdditionalSelectValue,
    additionalSelectValue,
    disabledOption,
    changeSelectValueToNonSelected,
    totalPercent,
    countTotalPercent,
    deleteFromTotal,
  } = useCostCenterPercentage(
    costCenters,
    selectedCostCenterId,
    additionalCostCenters,
    getValues,
    index,
  );
  const [selectedCustomDataDialog, setSelectedCustomDataDialog] = useState<
    null | number
  >(null);

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const CCHeaderRow = (CCHeaderProps: {
    children?: ReactNode;
    sx?: SxProps;
  }) => {
    const { children, sx } = CCHeaderProps;
    return (
      <TableRow>
        <TableCell colSpan={4} sx={{ ...sxStyles.tableHeaderRow, ...sx }}>
          {children}
        </TableCell>
      </TableRow>
    );
  };

  const CCTableRow = (CCTRProps: {
    nameField?: ReactNode;
    percentageField?: ReactNode;
    customData?: ReactNode;
    deleteButton?: ReactNode;
    sx?: SxProps;
    cellSx?: SxProps;
  }) => {
    const { nameField, percentageField, customData, deleteButton, sx, cellSx } =
      CCTRProps;

    return (
      <TableRow sx={sx}>
        <TableCell sx={cellSx}>{nameField}</TableCell>
        <TableCell align={'right'} sx={{ ...cellSx, width: '120px' }}>
          {percentageField}
        </TableCell>
        <TableCell
          sx={{
            ...cellSx,
            width: 0,
            minWidth: 'fit-content',
            whiteSpace: 'nowrap',
          }}
        >
          {customData}
        </TableCell>
        <TableCell align={'right'} sx={{ ...cellSx, width: '40px' }}>
          {deleteButton}
        </TableCell>
      </TableRow>
    );
  };

  const PercentField = ({
    cc,
    errorMessage,
  }: {
    cc: CostCenter | undefined;
    errorMessage: string;
  }) => {
    if (!cc) {
      return <Box sx={sxStyles.error}>{errorMessage}</Box>;
    }

    return (
      <Controller
        control={control}
        name={
          `schedule.${index}.empPosition.${cc.fCostCenterId}-cc-percent` as const
        }
        render={({ field: { onChange, value } }) => (
          <PercentageField
            onChange={(e) => {
              if (!isNaN(parseInt(e.target.value)) || e.target.value === '') {
                countTotalPercent(
                  value ? parseInt(value) || 0 : 0,
                  parseInt(e.target.value) || 0,
                );
                onChange(e.target.value);
              }
            }}
            value={value}
            type="text"
            size={'small'}
          />
        )}
      />
    );
  };

  const CustomDataButton = (CDBProps: { costCenterId: number | null }) => {
    const { costCenterId } = CDBProps;

    return (
      <Button
        size="small"
        variant="contained"
        onClick={() => setSelectedCustomDataDialog(costCenterId)}
      >
        {t('laCustomData')}
      </Button>
    );
  };

  return (
    <Paper>
      <Table size={'small'}>
        <TableBody>
          <CCHeaderRow sx={sxStyles.header}>{t('laCC')}</CCHeaderRow>
          <CCTableRow
            nameField={selectedCostCenterData?.fCostCenterName || ''}
            percentageField={PercentField({
              cc: selectedCostCenterData,
              errorMessage: t('error_fMainCostCenterInputRequired'),
            })}
            customData={
              <CustomDataButton
                costCenterId={selectedCostCenterData?.fCostCenterId || null}
              />
            }
          />
          <CCHeaderRow sx={sxStyles.header}>
            {t('fEmpAdditionalCostCenters')}
          </CCHeaderRow>
          <CCHeaderRow>
            <Box sx={sxStyles.additionalSelector}>
              <Autocomplete
                options={costCenters}
                getOptionLabel={(c) =>
                  `${c.fCostCenterCode} ${c.fCostCenterName}`
                }
                onChange={(e, value) => {
                  setAdditionalSelectValue(value?.fCostCenterId || -1);
                }}
                getOptionDisabled={(option) =>
                  disabledOption(option.fCostCenterId)
                }
                value={
                  costCenters.find(
                    (cc) => cc.fCostCenterId === additionalSelectValue,
                  ) || null
                }
                renderInput={(params) => <TextField {...params} />}
                size={'small'}
              />
              <Button
                disabled={
                  additionalCostCenters.length === costCenters.length - 1
                }
                onClick={() => {
                  onAdditionalCostCenterAdd(additionalSelectValue);
                  changeSelectValueToNonSelected();
                }}
                startIcon={<Add />}
                size={'small'}
              >
                {t('laAdd')}
              </Button>
            </Box>
          </CCHeaderRow>

          {additionalCostCenters.map((cc, index) => (
            <CCTableRow
              key={`additional-cc-row-${index}`}
              nameField={cc.fCostCenterName}
              percentageField={PercentField({
                cc,
                errorMessage: t('errorMessage'),
              })}
              deleteButton={
                <IconButton
                  onClick={() => {
                    if (!window.confirm(t('laConfirmRowDelete'))) {
                      return;
                    }
                    deleteFromTotal(cc.fCostCenterId);
                    deleteSelectedCC(cc.fCostCenterId);
                    changeSelectValueToNonSelected(cc.fCostCenterId);
                  }}
                  color={'error'}
                >
                  <Delete />
                </IconButton>
              }
              customData={
                <CustomDataButton costCenterId={cc?.fCostCenterId || null} />
              }
            />
          ))}
          <CCTableRow
            nameField={t('laTotal')}
            percentageField={`${totalPercent} %`}
            sx={sxStyles.tableHeaderRow}
            cellSx={{ fontWeight: 'bold' }}
          />
          {totalPercent > 100 && (
            <CCHeaderRow sx={sxStyles.error}>
              {t('laErrorAdditionalCostCentersPercentage')}
            </CCHeaderRow>
          )}
        </TableBody>
      </Table>
      {!!selectedCustomDataDialog && (
        <CustomDataDialogComponent
          open
          control={control}
          onClose={() => setSelectedCustomDataDialog(null)}
          customFields={customFields[selectedCustomDataDialog]}
        />
      )}
    </Paper>
  );
};
